/**
 * Documentos Fiscais — 5 sub-blocos por categoria (Federais / Municipais):
 *
 *   🔴 Atrasado     — venceu, sem pagamento encontrado
 *   🟡 Com atraso   — pago, mas fora do prazo
 *   🟢 Em dia       — pago dentro do prazo
 *   🔵 A vencer     — prazo ainda nao chegou
 *   ⚪ Justificados — override manual (pago por outro meio, etc.)
 *
 * REGRA: a data de emissao da NF define a competencia fiscal.
 * ISSQN vence dia 10 do mes seguinte (POA). DARF cod 5952 vence dia 20.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle, AlertTriangle, CheckCircle2,
  Hourglass, Loader2, ShieldCheck, X, RotateCcw, Flag,
} from "lucide-react";
import { api } from "../lib/api";
import { ChipPagina } from "./PaginaUI";
import { VisualizadorPaginas } from "./VisualizadorPaginas";

interface Props { competenciaId: number }

interface NfFiscal {
  numero: string | null;
  prestador_nome: string | null;
  municipio: string | null;
  data_emissao: string | null;
  mes_emissao: string | null;
  mes_vencimento: string | null;
  valor_servico: number | null;
  iss_valor: number | null;
  irrf_csll: number | null;
  pis_cofins: number | null;
  valor_liquido: number | null;
  fonte_pagina: string | null;
  competencia_origem: string | null;
  competencia_id?: number | null;            // competência da NF (p/ a lupa abrir no mês certo)
  documento_competencia_id?: number | null;  // competência do doc de pagamento
  status: string;
  status_msg: string;
  documento_pagina: string | null;
  duplicidade?: boolean;
  duplicidade_msg?: string | null;
}
interface DocPagamento {
  tipo: string;
  codigo_receita: string | null;
  municipio: string | null;
  valor: number | null;
  periodo_apuracao: string | null;
  mes_pa: string | null;
  data_vencimento: string | null;
  data_pagamento: string | null;
  fonte_pagina: string | null;
  competencia_balancete: string | null;
  competencia_id?: number | null;
}
interface SubBloco {
  nfs: NfFiscal[];
  documentos_pagamento: DocPagamento[];
  soma_retencoes: number;
  soma_pagamentos: number;
  diferenca: number;
}
interface JustificativaInfo {
  override_id: number;
  motivo: string;
  status_forcado: string;
  data_pagamento: string | null;
  criado_em: string;
}
interface NfJustificada extends NfFiscal {
  justificativa: JustificativaInfo;
}
interface BlocoFiscal {
  atrasado: SubBloco;
  com_atraso: SubBloco;
  em_dia: SubBloco;
  a_vencer: SubBloco;
  justificados: NfJustificada[];
}
interface Response {
  competencia_id: number;
  condominio_id: number;
  periodo_str: string;
  federais: BlocoFiscal;
  municipais: BlocoFiscal;
}

function fmtBRL(n: number | null | undefined): string {
  if (n == null) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
function fmtData(iso: string | null): string {
  if (!iso) return "—";
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a}`;
}

function Bullet({ cor, msg }: { cor: string; msg: string }) {
  const map: Record<string, string> = {
    verde: "bg-emerald-500",
    amarelo: "bg-amber-500",
    vermelho: "bg-rose-500",
    azul: "bg-sky-500",
    roxo: "bg-violet-600",
    cinza: "bg-slate-400",
  };
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${map[cor] || "bg-slate-400"}`} title={msg} />;
}

// ============================================================
// Modal — JUSTIFICAR NF MANUALMENTE
// ============================================================
interface JustificarModalProps {
  open: boolean;
  nf: NfFiscal | null;
  tipo: "FED" | "MUN";
  condominioId: number;
  competenciaId: number;
  onClose: () => void;
}

function JustificarModal({ open, nf, tipo, condominioId, competenciaId, onClose }: JustificarModalProps) {
  const [motivo, setMotivo] = useState("");
  const [statusForcado, setStatusForcado] = useState("PAGO_COM_ATRASO");
  const [dataPgto, setDataPgto] = useState("");
  const [confirmou, setConfirmou] = useState(false);
  const qc = useQueryClient();

  const mut = useMutation({
    mutationFn: () => api.post<unknown>(
      `/condominios/${condominioId}/overrides-fiscais`,
      {
        numero_nf: nf?.numero ?? "",
        data_emissao: nf?.data_emissao ?? "",
        tipo,
        status_forcado: statusForcado,
        motivo: motivo.trim(),
        data_pagamento: dataPgto || null,
      },
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fiscal-conciliado", competenciaId] });
      setMotivo(""); setDataPgto(""); setConfirmou(false);
      onClose();
    },
  });

  if (!open || !nf) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full overflow-hidden">
        <div className="bg-amber-50 border-b border-amber-200 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-600" />
            <h4 className="font-semibold text-slate-900">Justificar pagamento da NF</h4>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={18} />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3 text-sm">
          <div className="bg-slate-50 rounded p-3 text-xs space-y-1">
            <div><strong>NF nº:</strong> {nf.numero} — {nf.prestador_nome}</div>
            <div><strong>Emissão:</strong> {fmtData(nf.data_emissao)}</div>
            <div><strong>Tipo:</strong> {tipo === "FED" ? "Federal (DARF)" : "Municipal (ISSQN)"}</div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Motivo da justificativa <span className="text-rose-600">*</span>
            </label>
            <textarea
              value={motivo}
              onChange={e => setMotivo(e.target.value)}
              rows={3}
              placeholder="Ex.: Pago via GRU em 22/01/2026 — comprovante /comprovantes/jan26.pdf"
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Status forçado</label>
              <select
                value={statusForcado}
                onChange={e => setStatusForcado(e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white"
              >
                <option value="PAGO_EM_DIA">Pago em dia</option>
                <option value="PAGO_COM_ATRASO">Pago com atraso</option>
                <option value="OUTRO">Outro</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Data pgto (opcional)</label>
              <input
                type="date"
                value={dataPgto}
                onChange={e => setDataPgto(e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
              />
            </div>
          </div>

          {!confirmou ? (
            <div className="bg-amber-100 border-l-4 border-amber-500 px-3 py-2 text-xs text-amber-900">
              <strong>Tem certeza?</strong> Em contabilidade, isso fica registrado como override manual.
              A NF sairá dos bullets coloridos e irá para "Justificados manualmente" — com seu motivo
              visível para auditoria.
            </div>
          ) : null}

          <div className="flex gap-2 justify-end pt-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100 rounded"
            >Cancelar</button>
            {!confirmou ? (
              <button
                onClick={() => motivo.trim() && setConfirmou(true)}
                disabled={!motivo.trim()}
                className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >Continuar</button>
            ) : (
              <button
                onClick={() => mut.mutate()}
                disabled={mut.isPending}
                className="px-3 py-1.5 text-sm bg-rose-700 text-white rounded hover:bg-rose-800 disabled:opacity-50 inline-flex items-center gap-1"
              >
                {mut.isPending && <Loader2 size={12} className="animate-spin" />}
                Sim, justificar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Sub-bloco view (4 bullets coloridos)
// ============================================================
interface SubBlocoConfig {
  titulo: string;
  icone: typeof CheckCircle2;
  corIcone: string;
  fundo: string;
  msgVazio: string;
  corBullet: string;  // cor do bullet dentro do bloco (verde/amarelo/vermelho/cinza)
}

function SubBlocoView({
  sub, config, isMunicipal, mostraPagamento, onJustificar, onVerPagina,
}: {
  sub: SubBloco;
  config: SubBlocoConfig;
  isMunicipal: boolean;
  mostraPagamento: boolean;
  onJustificar: (nf: NfFiscal) => void;
  onVerPagina: (pagina: number, competenciaId?: number) => void;
}) {
  const corDiff = Math.abs(sub.diferenca) < 0.05 ? "text-emerald-700" : sub.diferenca > 0 ? "text-amber-700" : "text-rose-700";
  const Icone = config.icone;

  return (
    <div className={`rounded-lg border border-slate-200 overflow-hidden ${config.fundo}`}>
      <div className="px-4 py-2.5 bg-white border-b border-slate-200 flex items-center gap-2">
        <Icone size={16} className={config.corIcone} />
        <span className="text-sm font-semibold text-slate-800">{config.titulo}</span>
        <span className="text-xs text-slate-500 ml-auto tabular-nums">
          {sub.nfs.length} NF{sub.nfs.length !== 1 ? "s" : ""} · {fmtBRL(sub.soma_retencoes)}
        </span>
      </div>

      {sub.nfs.length === 0 ? (
        <div className="px-4 py-3 text-xs text-slate-500 text-center bg-white">{config.msgVazio}</div>
      ) : (
        <div className="overflow-x-auto bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2 text-center w-8">●</th>
                <th className="px-3 py-2 text-left">NF nº</th>
                <th className="px-3 py-2 text-left">Prestador</th>
                <th className="px-3 py-2 text-left">Emissão</th>
                <th className="px-3 py-2 text-left">Venc.</th>
                <th className="px-3 py-2 text-right">Serviço</th>
                {!isMunicipal ? (
                  <>
                    <th className="px-3 py-2 text-right">IRRF+CSLL</th>
                    <th className="px-3 py-2 text-right">PIS+COFINS</th>
                  </>
                ) : (
                  <th className="px-3 py-2 text-right">ISS</th>
                )}
                <th className="px-3 py-2 text-right">Líquido</th>
                <th className="px-3 py-2 text-center w-14">NF pág</th>
                {mostraPagamento && <th className="px-3 py-2 text-center w-16">Pgto pág</th>}
                <th className="px-3 py-2 text-center w-20"></th>
              </tr>
            </thead>
            <tbody>
              {sub.nfs.map((nf, k) => (
                <tr key={k} className={k % 2 === 0 ? "bg-white" : "bg-slate-50/40"}>
                  <td className="px-3 py-2 text-center"><Bullet cor={config.corBullet} msg={nf.status_msg} /></td>
                  <td className="px-3 py-2 font-semibold text-slate-900 tabular-nums">
                    <div className="inline-flex items-center gap-1">
                      {nf.numero ?? "—"}
                      {nf.duplicidade && (
                        <span title={nf.duplicidade_msg ?? "Pagamento duplicado"}>
                          <Flag size={14} className="text-rose-600 fill-rose-600" />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-700">
                    <div className="truncate max-w-[200px]" title={nf.prestador_nome ?? ""}>
                      {nf.prestador_nome ?? <span className="text-slate-400">—</span>}
                    </div>
                    {nf.municipio && <div className="text-[10px] text-slate-500">{nf.municipio}</div>}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-slate-600">{fmtData(nf.data_emissao)}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-600 text-[11px]">{nf.mes_vencimento ?? "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-900">{fmtBRL(nf.valor_servico)}</td>
                  {!isMunicipal ? (
                    <>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-800">{fmtBRL(nf.irrf_csll)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-800">{fmtBRL(nf.pis_cofins)}</td>
                    </>
                  ) : (
                    <td className="px-3 py-2 text-right tabular-nums text-slate-800">{fmtBRL(nf.iss_valor)}</td>
                  )}
                  <td className="px-3 py-2 text-right tabular-nums text-slate-900 font-medium">{fmtBRL(nf.valor_liquido)}</td>
                  <td className="px-3 py-2 text-center text-[10px]"><ChipPagina fonte={nf.fonte_pagina} onAbrir={(p) => onVerPagina(p, nf.competencia_id ?? undefined)} /></td>
                  {mostraPagamento && (
                    <td className="px-3 py-2 text-center text-[10px]"><ChipPagina fonte={nf.documento_pagina} onAbrir={(p) => onVerPagina(p, nf.documento_competencia_id ?? undefined)} /></td>
                  )}
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={() => onJustificar(nf)}
                      className="text-[10px] text-amber-700 hover:text-amber-900 hover:underline"
                      title="Justificar manualmente"
                    >Justificar</button>
                  </td>
                </tr>
              ))}
            </tbody>
            {mostraPagamento && sub.documentos_pagamento.length > 0 && (
              <tfoot>
                {sub.documentos_pagamento.map((d, k) => (
                  <tr key={k} className="bg-emerald-50/60 border-t border-emerald-200">
                    <td className="px-3 py-2 text-center"><span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-600" /></td>
                    <td colSpan={3} className="px-3 py-2 text-xs">
                      <span className="font-semibold text-emerald-900">{d.tipo}</span>
                      {d.codigo_receita && <span className="ml-2 text-slate-600">cód. {d.codigo_receita}</span>}
                      {d.periodo_apuracao && <span className="ml-2 text-slate-500">· PA {d.periodo_apuracao}</span>}
                      {d.competencia_balancete && <span className="ml-2 text-slate-400">· balancete {d.competencia_balancete}</span>}
                    </td>
                    <td className="px-3 py-2"></td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-900">{fmtBRL(d.valor)}</td>
                    <td colSpan={isMunicipal ? 1 : 2} className="px-3 py-2 text-center text-[11px] text-slate-700">
                      <div>Venc: {fmtData(d.data_vencimento)}</div>
                      <div className="text-slate-500">Pago: {fmtData(d.data_pagamento)}</div>
                    </td>
                    <td colSpan={2}></td>
                    <td className="px-3 py-2 text-center text-[10px]"><ChipPagina fonte={d.fonte_pagina} onAbrir={(p) => onVerPagina(p, d.competencia_id ?? undefined)} /></td>
                    <td></td>
                  </tr>
                ))}
                <tr className="bg-slate-50 border-t-2 border-slate-300 text-xs">
                  <td colSpan={5} className="px-3 py-2 text-right font-semibold text-slate-700">Diferença (pgtos − retenções):</td>
                  <td colSpan={isMunicipal ? 2 : 3} className={`px-3 py-2 text-right tabular-nums font-bold ${corDiff}`}>
                    {sub.diferenca >= 0 ? "+" : ""}{fmtBRL(sub.diferenca)}
                  </td>
                  <td colSpan={4}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Sub-bloco JUSTIFICADOS (5o bullet — cinza com motivo)
// ============================================================
function JustificadosView({
  justificados, isMunicipal, competenciaId,
}: {
  justificados: NfJustificada[];
  isMunicipal: boolean;
  condominioId: number;
  competenciaId: number;
}) {
  const qc = useQueryClient();
  const mutDel = useMutation({
    mutationFn: (overrideId: number) => api.delete<unknown>(`/overrides-fiscais/${overrideId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fiscal-conciliado", competenciaId] }),
  });

  if (justificados.length === 0) return null;

  return (
    <div className="rounded-lg border border-slate-300 overflow-hidden bg-slate-100/60">
      <div className="px-4 py-2.5 bg-white border-b border-slate-300 flex items-center gap-2">
        <ShieldCheck size={16} className="text-slate-600" />
        <span className="text-sm font-semibold text-slate-800">Justificados manualmente</span>
        <span className="text-xs text-slate-500 ml-auto tabular-nums">
          {justificados.length} NF{justificados.length !== 1 ? "s" : ""}
        </span>
      </div>
      <div className="bg-white divide-y divide-slate-200">
        {justificados.map((nf, k) => (
          <div key={k} className={`px-4 py-3 text-sm ${nf.duplicidade ? "bg-rose-50/60 border-l-4 border-rose-500" : ""}`}>
            {nf.duplicidade && (
              <div className="mb-2 inline-flex items-center gap-1.5 text-xs font-semibold text-rose-700 bg-rose-100 px-2 py-1 rounded">
                <Flag size={12} className="fill-rose-600" />
                <span title={nf.duplicidade_msg ?? ""}>PAGAMENTO DUPLICADO DETECTADO</span>
              </div>
            )}
            <div className="flex items-baseline justify-between gap-3">
              <div className="flex items-center gap-1">
                <span className="font-semibold text-slate-900">NF nº {nf.numero}</span>
                {nf.duplicidade && <Flag size={12} className="text-rose-600 fill-rose-600" />}
                <span className="ml-2 text-slate-600">{nf.prestador_nome}</span>
                <span className="ml-2 text-xs text-slate-500">Emissão {fmtData(nf.data_emissao)}</span>
              </div>
              <div className="text-xs text-slate-500 inline-flex items-center gap-2">
                <span className="px-2 py-0.5 rounded bg-slate-200">{nf.justificativa.status_forcado.replace(/_/g, " ").toLowerCase()}</span>
                <button
                  onClick={() => {
                    if (confirm("Reverter esta justificativa? A NF voltará aos bullets coloridos.")) {
                      mutDel.mutate(nf.justificativa.override_id);
                    }
                  }}
                  className="text-[10px] text-slate-500 hover:text-rose-700 hover:underline inline-flex items-center gap-0.5"
                  title="Reverter justificativa"
                >
                  <RotateCcw size={11} /> reverter
                </button>
              </div>
            </div>
            <div className="mt-1 text-xs text-slate-700 italic">
              "{nf.justificativa.motivo}"
            </div>
            <div className="mt-1 text-[10px] text-slate-400">
              {!isMunicipal ? `IRRF+CSLL ${fmtBRL(nf.irrf_csll)} · PIS+COFINS ${fmtBRL(nf.pis_cofins)}`
                            : `ISS ${fmtBRL(nf.iss_valor)}`}
              {nf.justificativa.data_pagamento && ` · pago em ${fmtData(nf.justificativa.data_pagamento)}`}
            </div>
            {nf.duplicidade && nf.duplicidade_msg && (
              <div className="mt-2 p-2 bg-rose-100/60 border border-rose-300 rounded text-[11px] text-rose-900">
                {nf.duplicidade_msg}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// Bloco (Federais ou Municipais)
// ============================================================
function BlocoVisao({
  bloco, titulo, subtitulo, corHeader, isMunicipal,
  tipo, condominioId, competenciaId, onJustificar, onVerPagina,
}: {
  bloco: BlocoFiscal;
  titulo: string;
  subtitulo: string;
  corHeader: string;
  isMunicipal: boolean;
  tipo: "FED" | "MUN";
  condominioId: number;
  competenciaId: number;
  onJustificar: (nf: NfFiscal, tipo: "FED" | "MUN") => void;
  onVerPagina: (pagina: number, competenciaId?: number) => void;
}) {
  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
      <div className={`px-5 py-3 ${corHeader} text-white`}>
        <div className="text-sm font-semibold">{titulo}</div>
        <div className="text-[11px] opacity-90">{subtitulo}</div>
      </div>
      <div className="p-3 space-y-3 bg-slate-50">
        <SubBlocoView
          sub={bloco.atrasado}
          config={{
            titulo: "Atrasado (sem pagamento)",
            icone: AlertTriangle,
            corIcone: "text-rose-700",
            fundo: "bg-rose-50/40",
            msgVazio: "Nenhuma NF atrasada — situação regular.",
            corBullet: "vermelho",
          }}
          isMunicipal={isMunicipal}
          mostraPagamento={false}
          onJustificar={nf => onJustificar(nf, tipo)}
          onVerPagina={onVerPagina}
        />
        <SubBlocoView
          sub={bloco.com_atraso}
          config={{
            titulo: "Pago com atraso",
            icone: AlertCircle,
            corIcone: "text-violet-700",
            fundo: "bg-violet-50/40",
            msgVazio: "Nenhum pagamento atrasado neste período.",
            corBullet: "roxo",
          }}
          isMunicipal={isMunicipal}
          mostraPagamento={true}
          onJustificar={nf => onJustificar(nf, tipo)}
          onVerPagina={onVerPagina}
        />
        <SubBlocoView
          sub={bloco.em_dia}
          config={{
            titulo: "Pago em dia",
            icone: CheckCircle2,
            corIcone: "text-emerald-700",
            fundo: "bg-emerald-50/40",
            msgVazio: "Nenhum pagamento em dia neste período.",
            corBullet: "verde",
          }}
          isMunicipal={isMunicipal}
          mostraPagamento={true}
          onJustificar={nf => onJustificar(nf, tipo)}
          onVerPagina={onVerPagina}
        />
        <SubBlocoView
          sub={bloco.a_vencer}
          config={{
            titulo: "A vencer (prazo ainda em aberto)",
            icone: Hourglass,
            corIcone: "text-amber-700",
            fundo: "bg-amber-50/40",
            msgVazio: "Nenhuma NF com prazo futuro.",
            corBullet: "amarelo",
          }}
          isMunicipal={isMunicipal}
          mostraPagamento={false}
          onJustificar={nf => onJustificar(nf, tipo)}
          onVerPagina={onVerPagina}
        />
        <JustificadosView
          justificados={bloco.justificados}
          isMunicipal={isMunicipal}
          condominioId={condominioId}
          competenciaId={competenciaId}
        />
      </div>
    </div>
  );
}

// ============================================================
// Componente principal
// ============================================================
export function DocumentosFiscaisSection({ competenciaId }: Props) {
  const { data, isLoading, error } = useQuery<Response>({
    queryKey: ["fiscal-conciliado", competenciaId],
    queryFn: () => api.get<Response>(`/competencias/${competenciaId}/fiscal-conciliado`),
  });

  const [modalNf, setModalNf] = useState<NfFiscal | null>(null);
  const [modalTipo, setModalTipo] = useState<"FED" | "MUN">("FED");
  const [paginaView, setPaginaView] = useState<{ pagina: number; competenciaId: number } | null>(null);

  if (isLoading) return (
    <section className="mt-8">
      <div className="flex items-center gap-2 text-slate-500 text-sm">
        <Loader2 size={16} className="animate-spin" /> Carregando documentos fiscais...
      </div>
    </section>
  );

  if (error || !data) return null;

  return (
    <section className="mt-8">
      <div className="rounded-t-xl bg-gradient-to-r from-emerald-700 to-teal-700 px-5 py-4 text-white shadow-sm">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <ShieldCheck size={18} /> Documentos Fiscais
            </h3>
            <p className="text-xs text-emerald-100/90 mt-0.5">
              Ciclo de retenções por data de emissão — competência {data.periodo_str}
            </p>
          </div>
          <div className="text-[11px] flex gap-3 items-center flex-wrap">
            <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-rose-400" /> atrasado</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-violet-500" /> pago com atraso</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400" /> pago em dia</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-400" /> a vencer</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-slate-300" /> justificado</span>
          </div>
        </div>
      </div>

      <div className="border-x border-b border-slate-200 rounded-b-xl bg-slate-50 px-4 py-4 space-y-4">
        <BlocoVisao
          bloco={data.federais}
          titulo="Tributos Federais"
          subtitulo="IRRF + CSLL + PIS + COFINS retidos → DARF cód. 5952 (vence dia 20 do mês seguinte à emissão)"
          corHeader="bg-gradient-to-r from-rose-700 to-orange-600"
          isMunicipal={false}
          tipo="FED"
          condominioId={data.condominio_id}
          competenciaId={competenciaId}
          onJustificar={(nf, t) => { setModalNf(nf); setModalTipo(t); }}
          onVerPagina={(pagina, cid) => setPaginaView({ pagina, competenciaId: cid ?? competenciaId })}
        />
        <BlocoVisao
          bloco={data.municipais}
          titulo="Tributos Municipais"
          subtitulo="ISSQN retido pelo tomador → Guia municipal (vence dia 10 do mês seguinte à emissão — Decreto POA 15.416/06)"
          corHeader="bg-gradient-to-r from-blue-800 to-indigo-700"
          isMunicipal={true}
          tipo="MUN"
          condominioId={data.condominio_id}
          competenciaId={competenciaId}
          onJustificar={(nf, t) => { setModalNf(nf); setModalTipo(t); }}
          onVerPagina={(pagina, cid) => setPaginaView({ pagina, competenciaId: cid ?? competenciaId })}
        />

        <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
          <div>
            <strong>Como ler:</strong> a regra é a <em>data de emissão</em> da NF. Vermelho = vencido sem pagto.
            Roxo = pago, mas em atraso. Verde = pago dentro do prazo. Amarelo = prazo futuro.
            Cinza = você justificou manualmente (ex.: pagamento por outro meio).
          </div>
        </div>
      </div>

      <JustificarModal
        open={modalNf !== null}
        nf={modalNf}
        tipo={modalTipo}
        condominioId={data.condominio_id}
        competenciaId={competenciaId}
        onClose={() => setModalNf(null)}
      />

      <VisualizadorPaginas
        competenciaId={paginaView?.competenciaId ?? competenciaId}
        paginas={paginaView != null ? [paginaView.pagina] : []}
        aberto={paginaView != null}
        onFechar={() => setPaginaView(null)}
      />
    </section>
  );
}
